import { File, Id, ResourceAcknowledgement } from '@nextforce/api'
import { Icon, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import {
  AcknowledgementSection,
  createSectionedList,
  getAcknowledgementSectionKey,
  getAcknowledgementSectionTitle,
  getDaysUntilDue,
  sortByAcknowledgementSection,
} from 'file_folder_repository/util'
import { FileListItem } from 'file_folder_repository/views/list_item'
import React, { useCallback, useMemo, useState } from 'react'
import { View } from 'react-native'
import Animated, {
  FadeInUp,
  FadeOutUp,
  LinearTransition,
} from 'react-native-reanimated'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { UserAvatar } from 'shared/components/user/user_avatar'
import { getLightShadeColor } from 'shared/utils'

interface Props {
  files: File[] | undefined
  onPressFileAcknowledgements: (fileId: Id) => void
}

export function ResourceAcknowledgementListView({
  files,
  onPressFileAcknowledgements,
}: Props) {
  const styles = useStyles()
  const listItems = useMemo(() => {
    if (!files) return []

    const sectionedFiles = [...files]
      .map(file => {
        const pendingAcks =
          file.resourceAcknowledgements?.filter(ack => !ack.isAcknowledged) ||
          []

        if (pendingAcks.length === 0) {
          return { file, sectionTitle: t('completed') }
        }

        const earliestDue = Math.min(
          ...pendingAcks.map(ack => new Date(ack.dueDate).getTime())
        )
        const daysUntilDue = getDaysUntilDue(earliestDue)
        const sectionTitle = getAcknowledgementSectionTitle(
          daysUntilDue,
          pendingAcks.length === 0
        )

        return { file, sectionTitle }
      })
      .sort(sortByAcknowledgementSection)

    return createSectionedList(sectionedFiles, item => ({
      type: 'file',
      file: item.file,
    }))
  }, [files])

  const renderItem = useCallback(
    ({ item }: { item: AcknowledgementSection }) => {
      if (item.type === 'header' && item.sectionTitle) {
        return (
          <Animated.View layout={LinearTransition}>
            <Text style={styles.sectionHeader}>{item.sectionTitle}</Text>
          </Animated.View>
        )
      }
      if (item.type === 'file' && !!item.file) {
        return (
          <AcknowledgementFileListItem
            file={item.file}
            onPressFileAcknowledgements={() =>
              onPressFileAcknowledgements(item.file.id)
            }
          />
        )
      }
      return null
    },
    [onPressFileAcknowledgements, styles.sectionHeader]
  )

  return (
    <View style={styles.container}>
      <Animated.FlatList
        data={listItems}
        renderItem={renderItem}
        keyExtractor={(item, index) =>
          getAcknowledgementSectionKey(item, index)
        }
        contentContainerStyle={styles.contentContainer}
        itemLayoutAnimation={LinearTransition}
      />
    </View>
  )
}

interface AcknowledgementFileListItemProps {
  file: File | undefined
  onPressFileAcknowledgements: () => void
}

function AcknowledgementFileListItem({
  file,
  onPressFileAcknowledgements,
}: AcknowledgementFileListItemProps) {
  const [expanded, setExpanded] = useState(false)
  const styles = useStyles()

  if (!file) {
    return null
  }

  return (
    <Animated.View style={styles.baseItemContainer} layout={LinearTransition}>
      <View style={styles.fileListItemRow}>
        <FileListItem
          file={file}
          onLongPress={() => null}
          onPress={() => null}
          metaDataType="none"
          titleDescriptionContainerStyle={styles.titleDescriptionContainer}
        />
        <PressableOpacity
          onPress={onPressFileAcknowledgements}
          style={styles.chevronContainer}
        >
          <Icon name="chevron-right" type="material-design" size={24} />
        </PressableOpacity>
      </View>
      <PressableOpacity onPress={() => setExpanded(!expanded)}>
        <ResourceAcknowlegementMembersSummaryRow
          file={file}
          expanded={expanded}
        />
      </PressableOpacity>
      {expanded && (
        <Animated.View entering={FadeInUp} exiting={FadeOutUp}>
          <ResourceAcknowlegementMemberList file={file} />
        </Animated.View>
      )}
    </Animated.View>
  )
}

function ResourceAcknowlegementMembersSummaryRow({
  file,
  expanded,
}: {
  file: File
  expanded: boolean
}) {
  const styles = useStyles()
  const { theme } = useTheme()

  const acknowledgements = file.resourceAcknowledgements || []
  const completedCount = acknowledgements.filter(
    ack => ack.isAcknowledged
  ).length
  const totalCount = acknowledgements.length

  return (
    <View style={styles.summaryRowContainer}>
      <View style={styles.acknowledgementInfoContainer}>
        <View
          style={[
            styles.acknowledgementCountContainer,
            {
              borderColor:
                completedCount === totalCount ? 'green' : theme.colors.primary,
            },
          ]}
        >
          <Text
            style={styles.countText}
          >{`${completedCount}/${totalCount}`}</Text>
        </View>
        <Text style={styles.statusText}>{t('acknowledged')}</Text>
      </View>
      <Icon
        name={expanded ? 'chevron-up' : 'chevron-down'}
        type="material-design"
        size={20}
        color={theme.colors.grey5}
      />
    </View>
  )
}

function ResourceAcknowlegementMemberList({ file }: { file: File }) {
  const styles = useStyles()
  return (
    <View style={styles.memberListContainer}>
      {file.resourceAcknowledgements?.map(acknowledgement => (
        <ResourceAcknowlegementMemberListItem
          acknowledgement={acknowledgement}
          key={acknowledgement.id}
        />
      ))}
    </View>
  )
}

function ResourceAcknowlegementMemberListItem({
  acknowledgement,
}: {
  acknowledgement: ResourceAcknowledgement
}) {
  const styles = useStyles()
  const member = acknowledgement.user
  const { theme } = useTheme()

  const dueDate = new Date(acknowledgement.dueDate)
  const isOverdue = dueDate < new Date() && !acknowledgement.isAcknowledged

  return (
    <View style={styles.memberItemContainer}>
      <View style={styles.memberDetailsRow}>
        <View style={styles.avatarSectionContainer}>
          <View style={styles.avatarContainer}>
            <UserAvatar user={member} showAvatarIfNoPhone />
          </View>
          <View>
            <Text>{`${member.firstName} ${member.lastName}`}</Text>
            <Text style={styles.dueDateText}>
              {`Due: ${dueDate.toLocaleDateString()}`}
            </Text>
          </View>
        </View>

        <View style={styles.statusContainer}>
          {acknowledgement.requireSign && (
            <View style={styles.signLabelContainer}>
              <Text style={styles.signLabelText}>Sign</Text>
              <Icon
                name="file-signature"
                type="font-awesome-5"
                solid
                size={16}
                color={theme.colors.grey1}
              />
            </View>
          )}
          <Icon
            name={
              acknowledgement.isAcknowledged ? 'checkcircle' : 'clockcircle'
            }
            size={24}
            color={
              acknowledgement.isAcknowledged
                ? theme.colors.success
                : isOverdue
                ? theme.colors.error
                : theme.colors.warning
            }
            type="antdesign"
          />
        </View>
      </View>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: { flex: 1, marginHorizontal: 5 },
  contentContainer: { flexGrow: 1 },
  baseItemContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey11 : getLightShadeColor(0.5),
    borderRadius: 15,
    marginTop: 5,
    paddingVertical: 5,
  },
  fileListItemRow: { flexDirection: 'row', alignItems: 'center' },
  chevronContainer: {
    padding: 10,
    marginRight: 10,
    borderRadius: 50,
  },
  summaryRowContainer: {
    paddingHorizontal: 15,
    paddingBottom: 5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  acknowledgementCountContainer: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 50,
    marginRight: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    backgroundColor: theme.colors.white,
  },
  memberListContainer: { marginTop: 5 },
  memberItemContainer: {
    paddingHorizontal: 10,
    marginHorizontal: 5,
    paddingVertical: 5,
    borderRadius: 15,
    marginBottom: 5,
    backgroundColor: theme.colors.white,
  },
  memberDetailsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 5,
  },
  avatarSectionContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatarContainer: { marginRight: 5 },
  sectionHeader: {
    fontSize: 16,
    fontWeight: 'bold',
    padding: 10,
  },
  acknowledgementInfoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dueDateText: {
    fontSize: 12,
    color: theme.colors.grey3,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  signatureIcon: {
    marginRight: 8,
  },
  countText: {
    color: theme.colors.black,
    fontWeight: 'bold',
  },
  statusText: {
    fontSize: 14,
    color: theme.colors.grey0,
  },
  titleDescriptionContainer: { justifyContent: 'center' },
  signLabelContainer: {
    flexDirection: 'row',
    borderWidth: 0.5,
    borderColor: 'grey',
    marginRight: 5,
    padding: 5,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
  },
  signLabelText: { marginRight: 5 },
}))
